<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.users.newUser.buttonText')"
      @click="newUserModalOpen = true"
    />
    <export
      url="/users/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.users')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <NewUserModal v-model:open="newUserModalOpen" @saved="refetch()" />
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="rowClickFunction"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import NewUserModal from './NewUserModal.vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import { useI18n } from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import {
  collOptionsProvider,
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@components/utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type { Region, HydraCollection, Address, User } from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import { useStore } from '@/store';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

const store = useStore();

const isSuperAdmin = store.getters['auth/IS_SUPER_ADMIN'];

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  noDrivers: 'true',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const newUserModalOpen = ref(false);

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

function rowClickFunction(user: User) {
  // debugger;
  if (user.roles.includes('ROLE_SUPER_ADMIN') && !isSuperAdmin) {
    return undefined;
  } else {
    return { name: 'user-details', params: { userId: user.id } };
  }
}

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.userList.fieldGroups.user'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('user.salutation'),
    group: t('pages.userList.fieldGroups.user'),
    formatter: (v: string) => t(`title.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.userList.filterGroups.user'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('user.salutation'),
      },
    },
  },
  {
    key: 'firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.userList.fieldGroups.user'),
    filter: {
      type: 'text',
      group: t('pages.userList.filterGroups.user'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'lastName',
    title: t('user.lastName'),
    group: t('pages.userList.fieldGroups.user'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.userList.filterGroups.user'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'email',
    title: t('user.email'),
    group: t('pages.userList.fieldGroups.user'),
    sortable: false,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.userList.filterGroups.user'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'roles',
    csvTranslations: {
      ROLE_ADMIN: t('user.roles.ROLE_ADMIN'),
      ROLE_SUPER_ADMIN: t('user.roles.ROLE_SUPER_ADMIN'),
      ROLE_MONITORING: t('user.roles.ROLE_MONITORING'),
      ROLE_DRIVER: t('user.roles.ROLE_DRIVER'),
      ROLE_USER: t('user.roles.ROLE_USER'),
      ROLE_COMPANY_USER: t('user.roles.ROLE_COMPANY_USER'),
      ROLE_VERIFIER: t('user.roles.ROLE_VERIFIER'),
      ROLE_HELPER: t('user.roles.ROLE_HELPER'),
      ROLE_VISIBILITY: t('user.roles.ROLE_VISIBILITY'),
    },
    title: t('user.role'),
    group: t('pages.userList.fieldGroups.account'),
    sortable: false,
    visible: true,
    formatter: (v: string[]) =>
      v
        .filter((el) => el !== 'ROLE_USER')
        .map((item) => t(`user.roles.${item}`))
        .join(', '),
    filter: {
      type: 'dropdown',
      group: t('pages.userList.filterGroups.account'),
      options: {
        options: enumOptionsProvider('/enums/NonDriverRole', (v) => ({
          label: t(`user.roles.${v}`),
          value: v,
        })),
        label: t('user.role'),
      },
    },
  },
  {
    key: 'phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.userList.fieldGroups.user'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.userList.filterGroups.user'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'language',
    title: t('user.language'),
    group: t('pages.userList.fieldGroups.user'),
    sortable: true,
    filter: {
      type: 'dropdown',
      group: t('pages.userList.filterGroups.user'),
      options: {
        options: enumOptionsProvider('/enums/language', (v) => ({
          value: v,
          label: t(`language.${v}`),
        })),
        label: t('user.language'),
      },
    },
  },
  {
    key: 'active',
    csvTranslations: {
      true: t('user.isActive.true'),
      false: t('user.isActive.false'),
    },
    title: t('user.active'),
    group: t('pages.userList.filterGroups.account'),
    sortable: false,
    visible: true,
    formatter: (v: boolean) => (v ? t('user.isActive.true') : t('user.isActive.false')),
    filter: {
      type: 'bool',
      group: t('pages.userList.filterGroups.account'),
      options: {
        label: t('user.active'),
        labelTrue: t('user.isActive.true'),
        labelFalse: t('user.isActive.false'),
      },
    },
  },
  {
    key: 'twoFactorEnabled',
    csvTranslations: {
      true: t('user.isActive.true'),
      false: t('user.isActive.false'),
    },
    title: t('user.twoFactorEnabled'),
    group: t('pages.userList.filterGroups.account'),
    sortable: false,
    visible: true,
    formatter: (v: boolean) => (v ? t('user.isActive.true') : t('user.isActive.false')),
    filter: {
      type: 'bool',
      group: t('pages.userList.filterGroups.account'),
      options: {
        label: t('user.twoFactorEnabled'),
        labelTrue: t('user.isActive.true'),
        labelFalse: t('user.isActive.false'),
      },
    },
  },
  {
    key: 'lastLogin',
    title: t('user.lastLogin'),
    group: t('pages.userList.fieldGroups.account'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.userList.filterGroups.account'),
      options: {
        label: t('user.lastLogin'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<User>>('/users', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
